import type { ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CardCanvas, CanvasPanel } from '@/components/patterns/nested-surface'
import { InfoPopover } from '@/components/patterns/infoPopover'

/**
 * Which `--chart-*` token to tint a metric's icon chip with. Applied by meaning rather than by page,
 * so a figure that appears on several screens (e.g. "Total Calls") always reads the same colour:
 *
 * - `--chart-1` blue  — people & relationships
 * - `--chart-2` green  — completed / positive outcomes
 * - `--chart-3` amber  — in-flight / time-bound
 * - `--chart-4` red    — needs attention
 * - `--chart-5` slate  — raw volume / neutral totals
 */
export type MetricCardProps = {
  icon?: ReactNode
  /** Tints the icon chip — a `--chart-*` token, per the convention above. Ignored without an icon. */
  tint?: string
  label: string
  value: ReactNode
  /** Tooltip on the figure — the exact, full-precision value when `value` itself is abbreviated
   * (e.g. `formatCompactCurrency`'s "1.2L" for a true ₹1,20,000). Omit entirely for a `value` that's
   * already exact (most callers) — this is never inferred from `value`'s content. */
  title?: string
  /** Signed delta pill beside the figure, e.g. `'17.0%'`. */
  trend?: string
  /** Drives the pill's colour and arrow direction. Green/up unless explicitly `false`. */
  trendUp?: boolean
  /** Explainer, set in its own inset well beneath the figure. */
  footnote?: ReactNode
  /** Small (i) tooltip beside the label. */
  info?: string
}

/**
 * The figure tile shared across the investor dashboard and the admin/staff stat grids: tinted icon
 * chip + all-caps label, the number, an optional delta pill, and the explainer in an inset well.
 *
 * Ported from `nivex-design/apps/nivex-web-v3/src/components/DashboardCards.tsx`.
 */
export function MetricCard({
  icon,
  tint,
  label,
  value,
  title,
  trend,
  trendUp,
  footnote,
  info,
}: MetricCardProps) {
  const chipTint = tint ?? 'var(--primary)'

  /* Tighter than CardCanvas's defaults, because a stat tile is mostly chrome: at the default
     8px padding + 8px gap the canvas alone spent 24px on a card whose content is a label, a
     number and one line of text.
   *
   * The radius moves with the padding on purpose. CardCanvas keeps its inner corners concentric
   * by the rule inner = outer − padding, so dropping padding to 6px without also dropping the
   * radius to 18px would leave CanvasPanel's 12px corners looking pinched inside a 20px frame. */
  return (
    <CardCanvas className="gap-1.5 rounded-[18px] p-1.5">
      {/* Surface 2 — the KPI. Raised off the canvas by its own fill and stroke, no shadow. */}
      <CanvasPanel className="flex flex-1 flex-col gap-2 p-3">
        {/* Label leads, icon tile sits opposite it, so the figure starts at the card's own edge
            rather than indented past a chip. */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="text-muted-foreground truncate text-[11px] font-semibold uppercase tracking-wide">
              {label}
            </span>
            {info ? (
              <InfoPopover
                contentClassName="w-56 text-center text-sm"
                trigger={
                  <button
                    type="button"
                    aria-label={`What does ${label} mean?`}
                    className="text-muted-foreground shrink-0"
                  >
                    <Info className="size-3" />
                  </button>
                }
              >
                {info}
              </InfoPopover>
            ) : null}
          </div>
          {icon ? (
            <span
              className="-mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[10px] border [&_svg]:size-4"
              style={{
                backgroundColor: `color-mix(in oklch, ${chipTint} 12%, transparent)`,
                borderColor: `color-mix(in oklch, ${chipTint} 24%, transparent)`,
                color: chipTint,
              }}
            >
              {icon}
            </span>
          ) : null}
        </div>

        {/* Pill sits directly beside the figure rather than pushed to the far edge, so the two read
            as one statement. */}
        <div className="flex flex-wrap items-center gap-2">
          {title ? (
            <InfoPopover
              contentClassName="w-fit text-sm"
              trigger={
                <span className="text-[26px] font-bold tabular-nums leading-none">{value}</span>
              }
            >
              {title}
            </InfoPopover>
          ) : (
            <span className="text-[26px] font-bold tabular-nums leading-none">{value}</span>
          )}
          {trend ? (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                trendUp === false ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600',
              )}
            >
              {trendUp === false ? (
                <ArrowDownRight className="size-3" />
              ) : (
                <ArrowUpRight className="size-3" />
              )}
              {trend}
            </span>
          ) : null}
        </div>
      </CanvasPanel>

      {/* Surface 3 — the insight. Same treatment as the KPI card but compact, so the two read as
          siblings on the canvas rather than as a card with a footer stuck to it. */}
      {footnote ? (
        <CanvasPanel className="text-muted-foreground px-3 py-2 text-xs leading-snug">
          {footnote}
        </CanvasPanel>
      ) : null}
    </CardCanvas>
  )
}

/**
 * Alias of `MetricCard` kept for the admin/staff stat grids, so those call sites read as `StatCard`
 * rather than all needing to be renamed. Same component, same props.
 */
export function StatCard({ label, value, trend, trendUp, footnote, icon, tint }: MetricCardProps) {
  return (
    <MetricCard
      icon={icon}
      tint={tint}
      label={label}
      value={value}
      trend={trend}
      trendUp={trendUp}
      footnote={footnote}
    />
  )
}
